import { useMemo, useState } from "react";
import styled from "styled-components";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import Formulario from "../../Componentes/Utils/Formulario";
import FormComp from "../../Componentes/FormComp";
import useApi from "../../hooks/useApi";
import useValidateColaborador from "../../hooks/useValidateColaborador";

const SectionTitle = styled.p`
  margin: 4px 0 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #263b4b;
`;

const InfoBox = styled.div`
  background: #f2f5ff;
  border-left: 4px solid #004aad;
  padding: 12px 14px;
  border-radius: 6px;
  font-size: 13px;
  color: #263b4b;
  line-height: 1.5;
  margin-top: 4px;
`;

const WarningBox = styled.div`
  background: #fef2f2;
  border-left: 4px solid #b91c1c;
  padding: 14px 16px;
  border-radius: 6px;
  font-size: 13px;
  color: #7f1d1d;
  line-height: 1.5;
  margin-top: 4px;

  strong {
    color: #7f1d1d;
  }
`;

const ListShell = styled.div`
  margin-top: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  max-height: 320px;
  overflow-y: auto;
  background: #fff;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 4px;
  }
`;

const ListHeader = styled.div`
  position: sticky;
  top: 0;
  display: grid;
  grid-template-columns: 70px 1fr 120px 120px;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #475569;
  text-transform: uppercase;
  z-index: 1;
`;

const EquipRow = styled.div`
  display: grid;
  grid-template-columns: 70px 1fr 120px 120px;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #eef2f7;
  font-size: 13px;
  color: #263b4b;

  &:last-child {
    border-bottom: none;
  }
`;

const EquipCell = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyRow = styled.div`
  padding: 18px 12px;
  text-align: center;
  font-size: 13px;
  color: #64748b;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 16px;
`;

const SecondaryButton = styled.button`
  all: unset;
  cursor: pointer;
  flex: 1;
  text-align: center;
  padding: 12px 0;
  border-radius: 8px;
  background: #e2e8f0;
  color: #263b4b;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;

  &:hover {
    background: #cbd5e1;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DangerButton = styled.button`
  all: unset;
  cursor: pointer;
  flex: 1;
  text-align: center;
  padding: 12px 0;
  border-radius: 8px;
  background: #b91c1c;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;

  &:hover {
    background: #991b1b;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export default function ModalOffboardColaborador({
  onClose,
  equipamentos,
  onOffboarded,
}) {
  const api = useApi();

  const [step, setStep] = useState("select");
  const [matriculaInput, setMatriculaInput] = useState("");
  const [saving, setSaving] = useState(false);

  const matriculaSelecionada = matriculaInput.trim();

  const {
    info: colabInfo,
    validating,
    error: validationError,
  } = useValidateColaborador(matriculaSelecionada, { bloquearDemitido: false });

  const equipamentosVinculados = useMemo(() => {
    if (!matriculaSelecionada) return [];
    return (equipamentos ?? []).filter(
      (e) => String(e.responsavel).trim() === matriculaSelecionada
    );
  }, [equipamentos, matriculaSelecionada]);

  const matriculaValida =
    matriculaSelecionada.length === 6 &&
    !validating &&
    !validationError &&
    Boolean(colabInfo);

  const avancarParaConfirmacao = (e) => {
    e.preventDefault();
    if (!matriculaValida || equipamentosVinculados.length === 0) return;
    setStep("confirm");
  };

  const confirmarOffboard = async () => {
    if (!matriculaValida) return;
    try {
      setSaving(true);
      await api.post("/Painel/API/InventarioTI/OffboardColaborador/", {
        matricula: matriculaSelecionada,
      });
      onClose?.();
      onOffboarded?.();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Erro ao realizar offboard.");
    } finally {
      setSaving(false);
    }
  };

  if (step === "select") {
    const semVinculos =
      matriculaValida && equipamentosVinculados.length === 0;

    return (
      <Overlay state={true}>
        <Modal header close={onClose} size="md">
          <Formulario onSubmit={avancarParaConfirmacao}>
            <FormComp
              title="MATRÍCULA DO COLABORADOR"
              type="numberOnly"
              placeholder="EX: 012345"
              valor={matriculaInput}
              name="matricula"
              changeFunc={(e) =>
                setMatriculaInput(e.target.value.replace(/\D/g, ""))
              }
              required
              maxLength={6}
            />
            <FormComp
              title="NOME"
              type="text"
              readOnly
              valor={colabInfo?.nome || ""}
              placeholder={validating ? "Validando matrícula..." : ""}
            />
            <FormComp
              title="FUNÇÃO"
              type="text"
              readOnly
              valor={colabInfo?.funcao || ""}
            />
            <FormComp
              title="CENTRO DE CUSTO"
              type="text"
              readOnly
              valor={
                colabInfo?.centro_custo_desc || colabInfo?.centro_custo || ""
              }
            />
            {validationError && (
              <p
                style={{ color: "#B91C1C", fontSize: "12px", marginTop: "-6px" }}
              >
                {validationError}
              </p>
            )}

            {matriculaValida && equipamentosVinculados.length > 0 && (
              <div>
                <SectionTitle>RESUMO</SectionTitle>
                <InfoBox>
                  Serão removidos{" "}
                  <strong>{equipamentosVinculados.length}</strong> equipamento
                  {equipamentosVinculados.length === 1 ? "" : "s"} vinculado
                  {equipamentosVinculados.length === 1 ? "" : "s"} à matrícula{" "}
                  <strong>{matriculaSelecionada}</strong>. Clique em AVANÇAR
                  para revisar e confirmar.
                </InfoBox>
              </div>
            )}

            {semVinculos && (
              <div>
                <SectionTitle>RESUMO</SectionTitle>
                <WarningBox>
                  Nenhum equipamento vinculado à matrícula{" "}
                  <strong>{matriculaSelecionada}</strong>.
                </WarningBox>
              </div>
            )}

            <FormComp
              type="submit"
              valor={validating ? "VALIDANDO..." : "AVANÇAR"}
              disabled={
                !matriculaValida ||
                validating ||
                equipamentosVinculados.length === 0
              }
            />
          </Formulario>
        </Modal>
      </Overlay>
    );
  }

  return (
    <Overlay state={true}>
      <Modal header close={saving ? undefined : onClose} size="lg">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SectionTitle>CONFIRMAÇÃO DE OFFBOARDING</SectionTitle>

          <WarningBox>
            Atenção: ao confirmar,{" "}
            <strong>TODOS os equipamentos abaixo</strong> serão desvinculados
            da matrícula <strong>{matriculaSelecionada}</strong>
            {colabInfo?.nome ? (
              <>
                {" "}
                (<strong>{colabInfo.nome}</strong>)
              </>
            ) : null}{" "}
            e marcados como disponíveis no inventário. Esta ação não pode ser
            desfeita automaticamente.
          </WarningBox>

          <div>
            <SectionTitle>
              EQUIPAMENTOS A SEREM REMOVIDOS ({equipamentosVinculados.length})
            </SectionTitle>
            <ListShell>
              <ListHeader>
                <span>ID</span>
                <span>Equipamento</span>
                <span>Patrimônio</span>
                <span>Nº Série</span>
              </ListHeader>

              {equipamentosVinculados.length === 0 ? (
                <EmptyRow>Nenhum equipamento vinculado.</EmptyRow>
              ) : (
                equipamentosVinculados.map((e) => (
                  <EquipRow key={e.id}>
                    <EquipCell title={e.id}>{e.id}</EquipCell>
                    <EquipCell title={e.descricao}>
                      {e.tipo !== "—" ? `${e.tipo} — ` : ""}
                      {e.descricao}
                    </EquipCell>
                    <EquipCell title={e.patrimonio}>{e.patrimonio}</EquipCell>
                    <EquipCell title={e.serial}>{e.serial}</EquipCell>
                  </EquipRow>
                ))
              )}
            </ListShell>
          </div>

          <ButtonRow>
            <SecondaryButton
              type="button"
              onClick={() => setStep("select")}
              disabled={saving}
            >
              VOLTAR
            </SecondaryButton>
            <DangerButton
              type="button"
              onClick={confirmarOffboard}
              disabled={saving || equipamentosVinculados.length === 0}
            >
              {saving
                ? "REMOVENDO..."
                : `CONFIRMAR OFFBOARDING (${equipamentosVinculados.length})`}
            </DangerButton>
          </ButtonRow>
        </div>
      </Modal>
    </Overlay>
  );
}
